@let i18n = translate();
@let control = fg.controls;
@let qrName = control.qrName;
@let expiryDate = control.expiryDate;
@let description = control.description;
@let onBoardingOptions = control.onBoardingOptions;
@let disabledForm = !fg.invalid;
@let testIdPrefix = 'rcp-vso-configure-qr';

@if (isBusy()) {
    <div
        class="rcp-fixed rcp-left-0 rcp-top-0 rcp-z-[60] rcp-m-auto rcp-flex rcp-h-screen rcp-w-screen rcp-flex-col rcp-items-center rcp-justify-center rcp-bg-[#ccc]/[50%]"
        data-testid="spinner-container"
    >
        <mat-spinner [strokeWidth]="6"></mat-spinner>
        {{ i18n.GeneratingYourCustomQrCode }}...
    </div>
} @else {
    <div class="rcp-flex rcp-w-full rcp-flex-col rcp-gap-4">
        <form
            class="rcp-px-5"
            [formGroup]="fg"
            [attr.data-testid]="'form' | testid: testIdPrefix"
        >
            <div class="rcp-grid rcp-grid-cols-1 rcp-gap-6">
                <mat-form-field
                    appearance="outline"
                    subscriptSizing="dynamic"
                >
                    <mat-label
                        [attr.data-testid]="'qrName-label' | testid: testIdPrefix"
                        >{{ i18n.QrCodeName }}</mat-label
                    >
                    <input
                        matInput
                        formControlName="qrName"
                        required
                        [attr.data-testid]="'qrName-input' | testid: testIdPrefix"
                    />
                    @if (qrName.hasError('required') || qrName.hasError('whitespace')) {
                        <mat-error
                            [attr.data-testid]="
                                'qrName-input-field-error' | testid: testIdPrefix
                            "
                            [interpolate]="i18n.MESSAGE__ThisFieldIsRequired"
                        >
                            <ng-template>{{ i18n.QrCodeName }}</ng-template>
                        </mat-error>
                    } @else if (qrName.hasError('maxlength')) {
                        <mat-error
                            [attr.data-testid]="
                                'qrName-input-field-error' | testid: testIdPrefix
                            "
                            [interpolate]="i18n.MESSAGE__ErrorMaxCharacters"
                        >
                            <ng-template>{{ i18n.QrCodeName }}</ng-template>
                            <ng-template>{{ qrNameMaxLength() + 1 }}</ng-template>
                        </mat-error>
                    } @else if (qrName.hasError('alphanumeric-only')) {
                        <mat-error
                            [attr.data-testid]="
                                'qrName-input-field-error' | testid: testIdPrefix
                            "
                            [interpolate]="i18n.MESSAGE__ErrorNameNoSpecialCharacters"
                        >
                            >
                            <ng-template>{{ i18n.QrCodeName }}</ng-template>
                        </mat-error>
                    }
                </mat-form-field>

                <mat-form-field
                    appearance="outline"
                    subscriptSizing="dynamic"
                    (click)="picker.open()"
                >
                    <mat-label
                        [attr.data-testid]="'expiry-date-label' | testid: testIdPrefix"
                        >{{ i18n.QrCodeExpirationDate }}</mat-label
                    >
                    <input
                        matInput
                        formControlName="expiryDate"
                        required
                        readonly="true"
                        [attr.data-testid]="'expiry-date-input' | testid: testIdPrefix"
                        [min]="minDate()"
                        [max]="maxDate()"
                        [matDatepicker]="picker"
                    />
                    <mat-hint class="rcp-uppercase">{{ dateFormat() }}</mat-hint>
                    <mat-datepicker-toggle
                        matIconSuffix
                        [for]="picker"
                    ></mat-datepicker-toggle>
                    <mat-datepicker #picker></mat-datepicker>
                    @if (expiryDate.hasError('matDatepickerParse')) {
                        <mat-error
                            [attr.data-testid]="
                                'expiryDate-input-field-error' | testid: testIdPrefix
                            "
                            [interpolate]="i18n.MESSAGE__FieldIsInvalid"
                        >
                            <ng-template>{{ i18n.QrCodeExpirationDate }}</ng-template>
                        </mat-error>
                    } @else if (expiryDate.hasError('required')) {
                        <mat-error
                            [attr.data-testid]="
                                'expiryDate-input-field-error' | testid: testIdPrefix
                            "
                            [interpolate]="i18n.MESSAGE__ThisFieldIsRequired"
                        >
                            <ng-template>{{ i18n.QrCodeExpirationDate }}</ng-template>
                        </mat-error>
                    } @else if (
                        expiryDate.hasError('matDatepickerMin') ||
                        expiryDate.hasError('matDatepickerMax')
                    ) {
                        <mat-error
                            [attr.data-testid]="
                                'expiryDate-input-field-error' | testid: testIdPrefix
                            "
                            [interpolate]="i18n.MESSAGE__FieldIsInvalid"
                        >
                            <ng-template>{{ i18n.QrCodeExpirationDate }}</ng-template>
                        </mat-error>
                    }
                </mat-form-field>

                <mat-form-field
                    appearance="outline"
                    subscriptSizing="dynamic"
                    class="rcp-w-full"
                >
                    <mat-label
                        [attr.data-testid]="'description-label' | testid: testIdPrefix"
                    >
                        {{ i18n.QrCodeDescription }}</mat-label
                    >
                    <textarea
                        matInput
                        rows="5"
                        formControlName="description"
                        [attr.data-testid]="'description-textarea' | testid: testIdPrefix"
                    ></textarea>
                    @if (description.hasError('maxlength')) {
                        <mat-error
                            [attr.data-testid]="
                                'description-textarea-field-error' | testid: testIdPrefix
                            "
                            [interpolate]="i18n.MESSAGE__ErrorMaxCharacters"
                        >
                            <ng-template>{{ i18n.QrCodeDescription }}</ng-template>
                            <ng-template>{{ descriptionMaxLength() + 1 }}</ng-template>
                        </mat-error>
                    }
                </mat-form-field>

                <div>
                    <mat-label
                        class="!rcp-mb-6 rcp-block rcp-text-sm rcp-font-bold"
                        [attr.data-testid]="
                            'onBoardingOptions-label' | testid: testIdPrefix
                        "
                        [interpolate]="i18n.SuppliersVendorsOnboardingRequired"
                    >
                        <ng-template>
                            <span class="!rcp-text-[#c02032]">*</span>
                        </ng-template>
                    </mat-label>
                    <mat-radio-group
                        aria-label="Select an option"
                        formControlName="onBoardingOptions"
                        class="!rcp-mb-10 rcp-block"
                        [attr.data-testid]="
                            'onBoardingOptions-radio-group' | testid: testIdPrefix
                        "
                    >
                        @for (item of approvalOptions(); track item.id) {
                            <mat-radio-button
                                #matRadioOnBoarding
                                class="rcp-mb-4 rcp-block"
                                name="onBoardingOptions"
                                [value]="item.id"
                                id="rad-{{ item.id }}"
                                [disabled]="item.disabled"
                                [attr.data-testid]="
                                    'onBoardingOptions-label-' +
                                        (onBoardingOptions.value === item.id.toString()
                                            ? 'selected-' + item.id
                                            : 'unselected-' + item.id)
                                        | testid: testIdPrefix + '-radio-group'
                                "
                            >
                                <label
                                    class="rcp-inline-flex rcp-h-full rcp-w-full rcp-cursor-pointer rcp-items-center"
                                    for="rad-{{ item.id }}"
                                    [ngClass]="{ '!rcp-cursor-default': item.disabled }"
                                >
                                    <div class="rcp-block">
                                        <div
                                            class="rcp-w-full rcp-text-sm rcp-font-semibold rcp-text-black"
                                            [attr.data-testid]="
                                                'onBoardingOptions-label-title-' + item.id
                                                    | testid
                                                        : testIdPrefix + '-radio-group'
                                            "
                                            [ngClass]="{
                                                'rcp-text-gray-500': item.disabled,
                                            }"
                                        >
                                            {{ item.title }}
                                        </div>
                                        <div
                                            class="rcp-w-full rcp-text-sm/5 rcp-text-ul-dark-100"
                                            [attr.data-testid]="
                                                'onBoardingOptions-label-description-' +
                                                    item.id
                                                    | testid
                                                        : testIdPrefix + '-radio-group'
                                            "
                                            [ngClass]="{
                                                'rcp-text-gray-400': item.disabled,
                                            }"
                                        >
                                            {{ item.description }}
                                        </div>
                                    </div>
                                </label>
                            </mat-radio-button>
                        }
                    </mat-radio-group>
                    @if (onBoardingOptions.hasError('required') && fg.touched) {
                        <small
                            class="-rcp-mt-[20px] rcp-block !rcp-text-[#c02032]"
                            [attr.data-testid]="
                                'onBoardingOptions-field-error' | testid: testIdPrefix
                            "
                            [interpolate]="i18n.MESSAGE__ThisFieldIsRequired"
                        >
                            <ng-template>{{
                                i18n.SuppliersVendorsOnboarding
                            }}</ng-template>
                        </small>
                    }
                </div>
            </div>
        </form>
        <div
            class="rcp-inline-flex rcp-w-full rcp-items-center rcp-justify-end rcp-gap-4 rcp-px-5"
        >
            <button
                mat-button
                [attr.data-testid]="'cancel-btn' | testid: testIdPrefix"
                [disabled]="isBusy()"
                (click)="onCancelClicked($event)"
            >
                {{ i18n.ACTION__Cancel }}
            </button>
            <button
                mat-flat-button
                [disabled]="!disabledForm || isBusy()"
                [attr.data-testid]="'generate-code-btn' | testid: testIdPrefix"
                (click)="onGenerateQrCode($event)"
            >
                {{ i18n.ACTION__GenerateCode }}
            </button>
        </div>
    </div>
}
